import { isNil } from 'es-toolkit';

import {
  canonicalToAcceptExtensions,
  extensionAliasToCanonical,
  extensionToMimeTypes,
  KB,
  MB,
  mimeAliasToCanonical,
} from './constants';

export function normalizeExtension(ext: string) {
  return ext.replace(/^\./, '').toLowerCase();
}

export function canonicalExtension(ext: string) {
  const n = normalizeExtension(ext);
  return extensionAliasToCanonical[n] ?? n;
}

export function getExtensionLabel(ext: string) {
  return ext === 'jpg' ? 'jpeg' : ext;
}

// Format: "X", "X or Y", "X, Y, or Z"
const disjunctionListFormat = new Intl.ListFormat('en', {
  style: 'long',
  type: 'disjunction',
});

// Format: "X", "X and Y", "X, Y, and Z"
const conjunctionListFormat = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

/** Same string returned by `formatAllowedExtensionsPhrase([])` for error-message branching. */
const NO_TYPES_CONFIGURED_PHRASE = 'no types configured';

function typeRestrictionParenthetical(allowedExtensionsPhrase: string) {
  if (allowedExtensionsPhrase === NO_TYPES_CONFIGURED_PHRASE) {
    return '(no types configured)';
  }

  return `(use ${allowedExtensionsPhrase})`;
}

/** Phrase for errors and UI, e.g. `pdf, jpeg, or png`, or `no types configured` when none allowed. */
export function formatAllowedExtensionsPhrase(allowedCanonicalExtensions: readonly string[]) {
  const labels = allowedCanonicalExtensions.map((ext) => getExtensionLabel(ext));
  if (labels.length === 0) {
    return NO_TYPES_CONFIGURED_PHRASE;
  }

  return disjunctionListFormat.format(labels);
}

export function formatQuotedFileNamesConjunction(files: readonly File[]) {
  return conjunctionListFormat.format(files.map((f) => `"${f.name}"`));
}

export type FileDropZoneErrors = {
  readonly files: {
    readonly tooLarge: readonly File[];
    readonly disallowed: readonly File[];
  };
  readonly info: {
    readonly maxSizeFormatted: string;
    readonly allowedExtensionsPhrase: string;
  };
};

/** User-facing message for rejected files (size / type), deduping files that fail both checks. */
export function formatFileError({ files, info }: FileDropZoneErrors) {
  const { tooLarge, disallowed } = files;
  const { maxSizeFormatted, allowedExtensionsPhrase } = info;

  const tooLargeSet = new Set(tooLarge);
  const disallowedSet = new Set(disallowed);

  const both = tooLarge.filter((f) => disallowedSet.has(f));
  const onlyTooLarge = tooLarge.filter((f) => !disallowedSet.has(f));
  const onlyDisallowed = disallowed.filter((f) => !tooLargeSet.has(f));

  const parts: string[] = [];

  if (onlyTooLarge.length > 0) {
    const list = formatQuotedFileNamesConjunction(onlyTooLarge);
    const exceedVerb = onlyTooLarge.length === 1 ? 'exceeds' : 'exceed';

    parts.push(`${list} ${exceedVerb} ${maxSizeFormatted}`);
  }

  if (both.length > 0) {
    const list = formatQuotedFileNamesConjunction(both);
    const exceedVerb = both.length === 1 ? 'exceeds' : 'exceed';
    const beVerb = both.length === 1 ? 'is' : 'are';

    parts.push(
      `${list} ${exceedVerb} ${maxSizeFormatted} and ${beVerb} not a supported type ${typeRestrictionParenthetical(allowedExtensionsPhrase)}`,
    );
  }

  if (onlyDisallowed.length > 0) {
    const list = formatQuotedFileNamesConjunction(onlyDisallowed);
    const beVerb = onlyDisallowed.length === 1 ? 'is' : 'are';

    parts.push(`${list} ${beVerb} not a supported type ${typeRestrictionParenthetical(allowedExtensionsPhrase)}`);
  }

  return parts.join('; ');
}

export function canonicalMime(mime: string) {
  const m = mime.trim().toLowerCase();
  return mimeAliasToCanonical[m] ?? m;
}

export function uniqueCanonicalExtensions(extensions: readonly string[]) {
  return [...new Set(extensions.map((e) => canonicalExtension(e)))];
}

export function buildAcceptAttribute(allowedCanonicalExtensions: ReadonlySet<string>) {
  const suffixes = new Set<string>();

  for (const canonical of allowedCanonicalExtensions) {
    const variants = canonicalToAcceptExtensions[canonical] ?? [canonical];
    for (const v of variants) {
      suffixes.add(normalizeExtension(v));
    }
  }
  return [...suffixes]
    .sort()
    .map((s) => `.${s}`)
    .join(',');
}

export function mimeTypesForCanonicalExtensions(canonicalExtensions: readonly string[]) {
  const out = new Set<string>();
  for (const ext of canonicalExtensions) {
    const list = extensionToMimeTypes[ext];
    if (!isNil(list)) {
      for (const m of list) {
        out.add(canonicalMime(m));
      }
    }
  }
  return [...out];
}

export function isFileAllowed(
  file: File,
  allowedCanonicalExtensions: ReadonlySet<string>,
  allowedCanonicalMimes: ReadonlySet<string>,
) {
  const rawExt = file.name.split('.').pop();
  const fileCanonicalExt = !isNil(rawExt) ? canonicalExtension(rawExt) : '';
  const extOk = fileCanonicalExt !== '' && allowedCanonicalExtensions.has(fileCanonicalExt);

  if (allowedCanonicalMimes.size === 0) {
    return extOk;
  }

  const mimeOk = file.type !== '' && allowedCanonicalMimes.has(canonicalMime(file.type));
  return extOk || mimeOk;
}

type ValidateFilesParams = {
  files: readonly File[];
  maxSizeBytes: number;
  allowedExtSet: ReadonlySet<string>;
  allowedMimeSet: ReadonlySet<string>;
  // Skips the type check entirely. Size is still enforced.
  unrestricted?: boolean;
};

export function validateFiles({
  files,
  maxSizeBytes,
  allowedExtSet,
  allowedMimeSet,
  unrestricted = false,
}: ValidateFilesParams) {
  const acceptedFile: File[] = [];
  const tooLargeFiles: File[] = [];
  const filesOfDisallowedType: File[] = [];

  for (const file of files) {
    const tooBig = file.size > maxSizeBytes;
    const typeAllowed = unrestricted || isFileAllowed(file, allowedExtSet, allowedMimeSet);

    if (tooBig) {
      tooLargeFiles.push(file);
    }
    if (!typeAllowed) {
      filesOfDisallowedType.push(file);
    }
    if (!tooBig && typeAllowed) {
      acceptedFile.push(file);
    }
  }

  return { acceptedFile, tooLargeFiles, filesOfDisallowedType };
}

export function getAllowedExtensionAndMimeSets(allowedExtensions?: readonly string[]) {
  const canonicalAllowed = uniqueCanonicalExtensions(allowedExtensions ?? []);
  const mimeList = mimeTypesForCanonicalExtensions(canonicalAllowed);
  return {
    allowedExtSet: new Set(canonicalAllowed),
    allowedMimeSet: new Set(mimeList),
    // Omitting the list means any type is accepted. Deliberately distinct from an empty list, which is
    // a caller that configured no types and wants every file refused.
    unrestricted: allowedExtensions === undefined,
  };
}

export function formatFileSize(bytes: number) {
  if (bytes < KB) {
    return `${bytes} B`;
  } else if (bytes < MB) {
    return `${(bytes / KB).toFixed(1)} KB`;
  }

  return `${(bytes / MB).toFixed(1)} MB`;
}

export function validateAndAcceptFiles({
  files,
  maxSizeBytes,
  allowedExtensions,
}: {
  readonly files: readonly File[];
  readonly maxSizeBytes: number;
  // Omit to accept any type. Size is still enforced.
  readonly allowedExtensions?: readonly string[];
}) {
  const { allowedExtSet, allowedMimeSet, unrestricted } = getAllowedExtensionAndMimeSets(allowedExtensions);
  const { acceptedFile, tooLargeFiles, filesOfDisallowedType } = validateFiles({
    files,
    maxSizeBytes,
    allowedExtSet,
    allowedMimeSet,
    unrestricted,
  });

  const errorMessage =
    tooLargeFiles.length > 0 || filesOfDisallowedType.length > 0
      ? formatFileError({
          files: { tooLarge: tooLargeFiles, disallowed: filesOfDisallowedType },
          info: {
            maxSizeFormatted: formatFileSize(maxSizeBytes),
            allowedExtensionsPhrase: formatAllowedExtensionsPhrase([...allowedExtSet]),
          },
        })
      : null;

  const accepted = acceptedFile.map((file) => ({
    id: crypto.randomUUID(),
    file,
  }));

  return { accepted, errorMessage };
}
